import { Box, Tooltip } from "@mui/material";
import { formatUsd } from "@platform/formatting-utils/formatters";

import { renewalTooltipLines } from "../utils/renewal-tooltip";

import { PREMIUM_TOOLTIP_SX } from "./premiumTooltipSx";

import type { FC } from "react";

const CELL_FONT_SIZE = "14px";
const CELL_BORDER = "1px solid var(--ag-border-color, #babfc7)";

export interface RenewalPremiumCellProps {
  currentPremium: number;
  /** Revised renewal when revised rates exist, else renewal. */
  renewalPremium: number;
  /** The renewal premium, only when revised renewal is what `renewalPremium` shows. */
  priorRenewalPremium?: number;
}

const subCellSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  px: 1,
  minHeight: "28px",
  fontSize: CELL_FONT_SIZE,
  whiteSpace: "nowrap",
} as const;

/**
 * Summary-row cell for a Current plan that has renewal rates: the current premium and the
 * renewal (or revised renewal) premium side by side, split evenly unless one
 * value needs more room. The current sub-cell hovers as its value alone, like every other premium; the renewal
 * sub-cell lists current, renewal and revised renewal.
 */
export const RenewalPremiumCell: FC<RenewalPremiumCellProps> = ({
  currentPremium,
  renewalPremium,
  priorRenewalPremium,
}) => {
  const tooltipSlotProps = { tooltip: { sx: PREMIUM_TOOLTIP_SX } };

  return (
    <Box
      sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", width: "100%" }}
    >
      <Tooltip
        title={formatUsd(currentPremium, 2)}
        placement="top"
        slotProps={tooltipSlotProps}
      >
        <Box sx={subCellSx}>{formatUsd(currentPremium, 0)}</Box>
      </Tooltip>
      <Tooltip
        title={renewalTooltipLines(
          currentPremium,
          renewalPremium,
          priorRenewalPremium,
        ).join("\n")}
        placement="top"
        slotProps={tooltipSlotProps}
      >
        <Box sx={{ ...subCellSx, borderLeft: CELL_BORDER }}>
          {formatUsd(renewalPremium, 0)}
        </Box>
      </Tooltip>
    </Box>
  );
};
